"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SERVER_DATABASE_ROOT_DOC_ID_LIKE_PATTERN = void 0;
exports.createServerDatabaseDocIdPrefix = createServerDatabaseDocIdPrefix;
exports.assertNonEmptyIdentifier = assertNonEmptyIdentifier;
exports.createServerDatabaseRowShardDocId = createServerDatabaseRowShardDocId;
exports.createServerDatabaseRowShardDocIdPrefix = createServerDatabaseRowShardDocIdPrefix;
exports.createServerDatabaseDirectoryPartDocIdPrefix = createServerDatabaseDirectoryPartDocIdPrefix;
exports.createServerDatabaseDirectoryPartDocId = createServerDatabaseDirectoryPartDocId;
exports.createServerDatabaseRootDocId = createServerDatabaseRootDocId;
exports.collectDatabaseStorageSyncDocIds = collectDatabaseStorageSyncDocIds;
exports.createLocalDatabaseRootDocId = createLocalDatabaseRootDocId;
exports.parseServerDatabaseRowShardDocId = parseServerDatabaseRowShardDocId;
exports.parseServerDatabaseDirectoryPartDocId = parseServerDatabaseDirectoryPartDocId;
exports.parseServerDatabaseRootDocId = parseServerDatabaseRootDocId;
exports.parseServerDatabaseDocId = parseServerDatabaseDocId;
exports.createServerPagePartDocIdPrefix = createServerPagePartDocIdPrefix;
exports.createServerPagePartDocId = createServerPagePartDocId;
exports.parseServerPagePartDocId = parseServerPagePartDocId;
exports.isServerPagePartDocId = isServerPagePartDocId;
exports.parseLocalDatabaseRootDocId = parseLocalDatabaseRootDocId;
exports.parseLocalDatabaseRowShardDocId = parseLocalDatabaseRowShardDocId;
exports.extractDatabaseIdFromRootDocId = extractDatabaseIdFromRootDocId;
exports.normalizeDatabaseBlockId = normalizeDatabaseBlockId;
exports.isServerDatabaseRowShardDocId = isServerDatabaseRowShardDocId;
exports.isServerDatabaseDirectoryPartDocId = isServerDatabaseDirectoryPartDocId;
exports.isServerDatabaseRootDocId = isServerDatabaseRootDocId;
exports.isLocalDatabaseRootDocId = isLocalDatabaseRootDocId;
exports.isLocalDatabaseRowShardDocId = isLocalDatabaseRowShardDocId;
exports.databaseDocumentCollectionRoot = databaseDocumentCollectionRoot;
const database_document_scheme_1 = require("./generated/database-document-scheme");
// The id scheme is owned by yrs-core (database_document_ids.rs); every
// segment below comes from the generated scheme module.
const SEP = database_document_scheme_1.DATABASE_DOC_ID_SEPARATOR;
const LOCAL_DATABASE_PREFIX = `${database_document_scheme_1.DATABASE_SYSTEM_DOC_PREFIX}${database_document_scheme_1.DATABASE_DOC_SEGMENT}${SEP}`;
const ROOT_SUFFIX = `${SEP}${database_document_scheme_1.DATABASE_ROOT_DOC_SEGMENT}`;
const ROWS_INFIX = `${SEP}${database_document_scheme_1.DATABASE_ROW_SHARD_DOC_SEGMENT}${SEP}`;
const DIR_INFIX = `${SEP}${database_document_scheme_1.DATABASE_DIRECTORY_PART_DOC_SEGMENT}${SEP}`;
const PAGE_PART_INFIX = `${SEP}${database_document_scheme_1.PAGE_PART_DOC_SEGMENT}${SEP}`;
function serverDatabasePrefix(workspaceId) {
    return `${database_document_scheme_1.DATABASE_SYSTEM_DOC_PREFIX}${workspaceId}${SEP}${database_document_scheme_1.DATABASE_DOC_SEGMENT}${SEP}`;
}
/** Common prefix of every database root and row-shard document of a workspace. */
function createServerDatabaseDocIdPrefix(workspaceId) {
    assertNonEmptyIdentifier(workspaceId, 'Workspace id');
    return serverDatabasePrefix(workspaceId);
}
function isIdentifier(value) {
    return !!value && !value.includes(SEP);
}
function assertNonEmptyIdentifier(value, label) {
    if (!isIdentifier(value)) {
        throw new Error(`${label} must be a non-empty identifier without "${SEP}"`);
    }
}
function createServerDatabaseRowShardDocId({ workspaceId, databaseId, shardId, }) {
    assertNonEmptyIdentifier(workspaceId, 'Workspace id');
    assertNonEmptyIdentifier(databaseId, 'Database id');
    assertNonEmptyIdentifier(shardId, 'Database row-shard id');
    return `${createServerDatabaseRowShardDocIdPrefix({ workspaceId, databaseId })}${shardId}`;
}
/** Common prefix of every row-shard document of one database. */
function createServerDatabaseRowShardDocIdPrefix({ workspaceId, databaseId, }) {
    assertNonEmptyIdentifier(workspaceId, 'Workspace id');
    assertNonEmptyIdentifier(databaseId, 'Database id');
    return `${serverDatabasePrefix(workspaceId)}${databaseId}${ROWS_INFIX}`;
}
/** Common prefix of every directory part document of one database. */
function createServerDatabaseDirectoryPartDocIdPrefix({ workspaceId, databaseId, }) {
    assertNonEmptyIdentifier(workspaceId, 'Workspace id');
    assertNonEmptyIdentifier(databaseId, 'Database id');
    return `${serverDatabasePrefix(workspaceId)}${databaseId}${DIR_INFIX}`;
}
function createServerDatabaseDirectoryPartDocId({ workspaceId, databaseId, partId, }) {
    assertNonEmptyIdentifier(partId, 'Database directory part id');
    return `${createServerDatabaseDirectoryPartDocIdPrefix({ workspaceId, databaseId })}${partId}`;
}
function createServerDatabaseRootDocId({ workspaceId, databaseId, }) {
    assertNonEmptyIdentifier(workspaceId, 'Workspace id');
    assertNonEmptyIdentifier(databaseId, 'Database id');
    return `${serverDatabasePrefix(workspaceId)}${databaseId}${ROOT_SUFFIX}`;
}
/** SQL LIKE pattern that matches the root document of every database. */
exports.SERVER_DATABASE_ROOT_DOC_ID_LIKE_PATTERN = `${database_document_scheme_1.DATABASE_SYSTEM_DOC_PREFIX}%${SEP}${database_document_scheme_1.DATABASE_DOC_SEGMENT}${SEP}%${ROOT_SUFFIX}`;
/**
 * Database system documents required before a server-side database operation.
 * Known storage docs win; a fresh database deterministically falls back to its
 * root and first row shard once a workspace id is available.
 */
function collectDatabaseStorageSyncDocIds({ databaseId, databaseRootDocId, databaseDirectoryPartDocIds, databaseRowShardDocIds, workspaceId, }) {
    const docIds = new Set();
    const add = (docId) => {
        if (docId)
            docIds.add(docId);
    };
    add(databaseRootDocId ??
        (workspaceId
            ? createServerDatabaseRootDocId({ workspaceId, databaseId })
            : undefined));
    // The server reads the shard list from the root and its directory parts.
    databaseDirectoryPartDocIds?.forEach(add);
    const rowShardDocIds = databaseRowShardDocIds?.filter((docId) => typeof docId === 'string' && docId.length > 0);
    if (rowShardDocIds?.length) {
        rowShardDocIds.forEach(add);
    }
    else if (workspaceId) {
        add(createServerDatabaseRowShardDocId({
            workspaceId,
            databaseId,
            shardId: database_document_scheme_1.DATABASE_DEFAULT_ROW_SHARD_ID,
        }));
    }
    return [...docIds];
}
function createLocalDatabaseRootDocId({ databaseId, }) {
    assertNonEmptyIdentifier(databaseId, 'Database id');
    return `${LOCAL_DATABASE_PREFIX}${databaseId}${ROOT_SUFFIX}`;
}
function stripRootSuffix(rest) {
    if (rest === null || !rest.endsWith(ROOT_SUFFIX))
        return null;
    const databaseId = rest.slice(0, -ROOT_SUFFIX.length);
    return isIdentifier(databaseId) ? databaseId : null;
}
/** `<database><infix><id>` with both parts plain identifiers. */
function splitInfix(rest, infix) {
    if (rest === null)
        return null;
    const at = rest.indexOf(infix);
    if (at < 0)
        return null;
    const databaseId = rest.slice(0, at);
    const id = rest.slice(at + infix.length);
    return isIdentifier(databaseId) && isIdentifier(id)
        ? { databaseId, id }
        : null;
}
function splitRowShard(rest) {
    const parts = splitInfix(rest, ROWS_INFIX);
    return parts && { databaseId: parts.databaseId, shardId: parts.id };
}
function stripPrefix(docId, prefix) {
    return docId.startsWith(prefix) ? docId.slice(prefix.length) : null;
}
function parseServerDatabaseRowShardDocId(docId, workspaceId) {
    const parts = splitRowShard(stripPrefix(docId, serverDatabasePrefix(workspaceId)));
    return parts ? { workspaceId, ...parts } : null;
}
function parseServerDatabaseDirectoryPartDocId(docId, workspaceId) {
    const parts = splitInfix(stripPrefix(docId, serverDatabasePrefix(workspaceId)), DIR_INFIX);
    return parts
        ? { workspaceId, databaseId: parts.databaseId, partId: parts.id }
        : null;
}
function parseServerDatabaseRootDocId(docId, workspaceId) {
    const databaseId = stripRootSuffix(stripPrefix(docId, serverDatabasePrefix(workspaceId)));
    return databaseId ? { workspaceId, databaseId } : null;
}
/** Parses a server root, row-shard or directory part document id of any
 * workspace. */
function parseServerDatabaseDocId(docId) {
    const rest = stripPrefix(docId, database_document_scheme_1.DATABASE_SYSTEM_DOC_PREFIX);
    if (rest === null)
        return null;
    const parts = rest.split(SEP);
    if (parts[1] !== database_document_scheme_1.DATABASE_DOC_SEGMENT)
        return null;
    const [workspaceId, , databaseId] = parts;
    if (!isIdentifier(workspaceId) || !isIdentifier(databaseId))
        return null;
    if (parts.length === 4 && parts[3] === database_document_scheme_1.DATABASE_ROOT_DOC_SEGMENT) {
        return { workspaceId, databaseId, kind: 'root' };
    }
    if (parts.length !== 5 || !isIdentifier(parts[4]))
        return null;
    if (parts[3] === database_document_scheme_1.DATABASE_ROW_SHARD_DOC_SEGMENT) {
        return { workspaceId, databaseId, kind: 'row-shard', shardId: parts[4] };
    }
    if (parts[3] === database_document_scheme_1.DATABASE_DIRECTORY_PART_DOC_SEGMENT) {
        return {
            workspaceId,
            databaseId,
            kind: 'directory-part',
            partId: parts[4],
        };
    }
    return null;
}
/** Common prefix of every part document of one page. */
function createServerPagePartDocIdPrefix({ workspaceId, pageId, }) {
    assertNonEmptyIdentifier(workspaceId, 'Workspace id');
    assertNonEmptyIdentifier(pageId, 'Page id');
    return `${database_document_scheme_1.DATABASE_SYSTEM_DOC_PREFIX}${workspaceId}${SEP}${database_document_scheme_1.PAGE_DOC_SEGMENT}${SEP}${pageId}${PAGE_PART_INFIX}`;
}
function createServerPagePartDocId({ workspaceId, pageId, partId, }) {
    assertNonEmptyIdentifier(partId, 'Page part id');
    return `${createServerPagePartDocIdPrefix({ workspaceId, pageId })}${partId}`;
}
/** Parses a server page part document id of any workspace. */
function parseServerPagePartDocId(docId) {
    const rest = stripPrefix(docId, database_document_scheme_1.DATABASE_SYSTEM_DOC_PREFIX);
    if (rest === null)
        return null;
    const parts = rest.split(SEP);
    if (parts.length !== 5 ||
        parts[1] !== database_document_scheme_1.PAGE_DOC_SEGMENT ||
        parts[3] !== database_document_scheme_1.PAGE_PART_DOC_SEGMENT) {
        return null;
    }
    const [workspaceId, , pageId, , partId] = parts;
    return isIdentifier(workspaceId) &&
        isIdentifier(pageId) &&
        isIdentifier(partId)
        ? { workspaceId, pageId, partId }
        : null;
}
function isServerPagePartDocId(docId) {
    return parseServerPagePartDocId(docId) !== null;
}
function parseLocalDatabaseRootDocId(docId) {
    const databaseId = stripRootSuffix(stripPrefix(docId, LOCAL_DATABASE_PREFIX));
    return databaseId ? { databaseId } : null;
}
function parseLocalDatabaseRowShardDocId(docId) {
    return splitRowShard(stripPrefix(docId, LOCAL_DATABASE_PREFIX));
}
function extractDatabaseIdFromRootDocId(docId) {
    const local = parseLocalDatabaseRootDocId(docId);
    if (local)
        return local.databaseId;
    const server = parseServerDatabaseDocId(docId);
    return server?.kind === 'root' ? server.databaseId : null;
}
function normalizeDatabaseBlockId(value) {
    return extractDatabaseIdFromRootDocId(value) ?? value;
}
function isServerDatabaseRowShardDocId(docId, workspaceId) {
    return parseServerDatabaseRowShardDocId(docId, workspaceId) !== null;
}
function isServerDatabaseDirectoryPartDocId(docId, workspaceId) {
    return parseServerDatabaseDirectoryPartDocId(docId, workspaceId) !== null;
}
function isServerDatabaseRootDocId(docId, workspaceId) {
    return parseServerDatabaseRootDocId(docId, workspaceId) !== null;
}
function isLocalDatabaseRootDocId(docId) {
    return parseLocalDatabaseRootDocId(docId) !== null;
}
function isLocalDatabaseRowShardDocId(docId) {
    return parseLocalDatabaseRowShardDocId(docId) !== null;
}
/**
 * Root type of a top-level collection of a database document: the row order
 * of a row shard and the entries of a directory part are arrays, every other
 * collection is a map.
 */
function databaseDocumentCollectionRoot(name) {
    return name === database_document_scheme_1.DATABASE_ROW_SHARD_ORDER_ARRAY_KEY ||
        name === database_document_scheme_1.DATABASE_DIRECTORY_PART_SHARDS_ARRAY_KEY
        ? 'array'
        : 'map';
}
